import {createSlice, PayloadAction} from '@reduxjs/toolkit';

import {Favorite, FavoriteTag} from '~shared/models';

import {
    BasePanelState,
    init,
    loadNextPage,
    makeAddItems,
    makeChangeOrder,
    makeReset,
    makeSetItems,
    setError,
    setLoading,
    setLoadingMore,
    setQueryFilter,
} from '../common';

import {
    getFavoriteTags,
    deleteFavorite,
    createFavoriteTags,
    createFavorite,
    updateFavorite,
} from './thunks';

export type FavoriteTableData = ReturnType<typeof toTableData>;

interface FavoriteState extends BasePanelState<FavoriteTableData, FavoriteFiltersState> {
    tags: FavoriteTag[];
}

interface FavoriteFiltersState {
    tags: string[];
}

const initialState: FavoriteState = {
    items: [],
    isFirstPage: true,
    nextCursor: null,
    loading: false,
    loadingMore: false,
    isEmpty: true,
    tags: [],
    filters: {
        query: '',
        tags: [],
    },
    orderDirection: 'desc',
    orderBy: 'createdAt',
};

export const {reducer, actions} = createSlice({
    name: 'favorites',
    initialState,
    reducers: {
        init,
        reset: makeReset(initialState),
        loadNextPage,
        changeOrder: makeChangeOrder('createdAt'),
        setItems: makeSetItems(toTableData),
        addItems: makeAddItems(toTableData),
        setLoading,
        setLoadingMore,
        setError,
        setQueryFilter,

        setTagsFilter(state, action: PayloadAction<string[]>) {
            state.loading = true;
            state.nextCursor = null;
            state.isFirstPage = true;
            state.filters.tags = action.payload;
        },
    },
    extraReducers: (builder) =>
        builder
            .addCase(createFavorite.fulfilled, (state, {payload}) => {
                const favorite = toTableData(payload);
                const item = state.items.find((item) => item.slug === favorite.slug);
                if (item) {
                    Object.assign(item, favorite);
                } else {
                    // TODO should be unreal case right now
                    // gracefully degrade if we reach this code
                    // (p.s. it may break the sorting)
                    state.items.unshift(favorite);
                }
            })
            .addCase(getFavoriteTags.fulfilled, (state, action) => {
                state.tags = action.payload.results;
            })
            .addCase(deleteFavorite.pending, (state, {meta}) => {
                const deleteItemId = meta.arg.favoriteId;
                const item = state.items.find((item) => item.id === deleteItemId);

                if (!item) return;

                item.isRemoved = true;
            })
            .addCase(deleteFavorite.fulfilled, (state) => {
                state.items = state.items.filter((item) => {
                    // if the favorite page has been deleted
                    // we remove it from the list completely
                    if (item.isRemoved && !item.isActive) {
                        return false;
                    }

                    return true;
                });
            })
            .addCase(deleteFavorite.rejected, (state, {meta}) => {
                const deleteItemId = meta.arg.favoriteId;
                const item = state.items.find((item) => item.id === deleteItemId);

                if (!item) return;

                item.isRemoved = false;
            })
            .addCase(updateFavorite.fulfilled, (state, {payload}) => {
                const favorite = toTableData(payload);
                const favoriteTags = favorite.tags;

                const newTags = getNewTags(state.tags, favoriteTags);
                state.tags.push(...newTags);

                const item = state.items.find((item) => item.id === favorite.id);

                if (!item) return;

                Object.assign(item, favorite);
            })
            .addCase(createFavoriteTags.fulfilled, (state, {payload}) => {
                state.tags.push(...payload.results);
            }),
});

function getNewTags(existingTags: FavoriteTag[], itemTags: FavoriteTag[]) {
    const existingTagsIds = existingTags.reduce((acc, current) => {
        acc.add(current.id);
        return acc;
    }, new Set<number>());

    return itemTags.filter((tag) => !existingTagsIds.has(tag.id));
}

// TODO store all page data in the `pages` slice
function toTableData(favorite: Favorite) {
    return {
        id: favorite.id,
        author: favorite.page.author,
        title: favorite.page.title,
        slug: favorite.page.slug,
        tags: favorite.tags,
        pageModifiedAt: favorite.page.modified_at,
        pageCreatedAt: favorite.page.created_at,
        isActive: favorite.page.is_active,
        isRemoved: false,
    };
}
